import type { Route } from '../shell/routes';
import { isActivityType } from './activity-types';
import type { ActivityType } from './activity-types';

export const PAGE_SIZE = 50;

/** What the Activity page shows, read from `#/activity?type=error&page=2`. */
export interface ActivityView {
  readonly type: ActivityType | undefined;
  readonly page: number;
}

export function readActivityView(route: Route): ActivityView {
  // `#/activity/blocked` was the old address of a filter; it still works.
  const raw = route.query?.['type'] ?? route.action;
  const page = Number.parseInt(route.query?.['page'] ?? '1', 10);
  return {
    type: isActivityType(raw) ? raw : undefined,
    page: Number.isFinite(page) && page >= 1 ? page : 1,
  };
}

export function activityRoute(view: ActivityView): Route {
  const query: Record<string, string> = {};
  if (view.type !== undefined) query['type'] = view.type;
  if (view.page > 1) query['page'] = String(view.page);
  return { page: 'activity', ...(Object.keys(query).length === 0 ? {} : { query }) };
}

export function pageCount(total: number, size = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / size));
}

/**
 * The page buttons to draw: the first, the last, and two either side of the
 * current one, with a gap (`null`) wherever pages are skipped.
 */
export function pageWindow(current: number, pages: number): (number | null)[] {
  const shown = new Set([1, pages, current - 2, current - 1, current, current + 1, current + 2]);
  const sorted = [...shown].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  const out: (number | null)[] = [];
  for (const p of sorted) {
    const prev = out.at(-1);
    if (typeof prev === 'number' && p - prev > 1) out.push(null);
    out.push(p);
  }
  return out;
}
